
    // IMPORTS
    import { computed, defineComponent, ref, watchEffect } from 'vue'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'

    import { TextBox, InputSeparator } from '@progress/kendo-vue-inputs'
    import MyIcon from '@/components/common/my-icon.vue'

    import { IMaterialDTO } from '@/types/material'
    import { buildLocalization, buildMaterialLocalization, StringUtil } from '@/util'

    interface ISearchTarget {
        searchText?: string,
        searchNumber?: number,
        searchRegex?: RegExp | null
    }

    export default defineComponent({
        name: 'sparepartSearchFilter',

        components: {
            MyIcon,
            'k-textbox': TextBox,
            InputSeparator
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChange: (searchText: string) => true
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparePartFilterStore = useSparePartFilterStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const searchText = ref<string>('')
            const publicData = { searchText }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const searchState = computed(() => sparePartFilterStore.searchState)
            const anyActiveSearch = computed(() => StringUtil.hasContent(searchText.value))

            const searchTarget = computed<ISearchTarget | null>(() => anyActiveSearch.value
                ? {
                    searchText: searchText.value?.trim().toLowerCase(),
                    searchNumber: parseInt(searchText.value),
                    searchRegex: getSearchRegex(searchText.value?.trim())
                }
                : null)

            const computedData = { searchState }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function onChanged() {
                onSearch()
            }

            function onSearch() {
                const target = searchTarget.value

                sparePartFilterStore.setSearchState({
                    text: searchText.value,
                    searchFunction: target == null
                        ? null
                        : (material: IMaterialDTO) => materialMatchesFilter(target, material)
                })    

                emit('onChange', searchText.value)
            }

            const publicMethods = { localize, onChanged }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            const { materialTextFor, materialSalesTextFor } = buildMaterialLocalization()


            function getSearchRegex(searchText: string): RegExp | null {
                if (!searchText.includes('*')) return null

                const pattern = searchText
                    .replaceAll(/[/\-\\^$+?.()|[\]{}]/g, '\\$&')
                    .replaceAll('*', '.*')

                return new RegExp(pattern, 'i') // case-insensitive matching
            }

            function materialMatchesFilter(filter: ISearchTarget, material: IMaterialDTO): boolean {
                return (material.materialNo && searchMatchesNumber(filter, material.materialNo)) ||
                    searchMatchesText(filter, materialTextFor(material)) ||
                    searchMatchesText(filter, materialSalesTextFor(material))
            }

            function searchMatchesNumber(filter: ISearchTarget, n: number): boolean {
                return n === filter?.searchNumber
            }

            function searchMatchesText(filter: ISearchTarget, text: string): boolean {
                text = text.trim()

                return (filter.searchRegex != null && filter.searchRegex.test(text)) ||
                       (filter.searchText != null && text.toLowerCase().includes(filter.searchText))
            }

            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            // Load initial search text (and make sure that any external changes are reflected afterwards)
            watchEffect(() => {
                searchText.value = sparePartFilterStore.searchState.text ?? ''
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
